import { PERSIST, TERRAIN, PLAYER, SURVIVAL } from '../config';
import { ITEMS, type ItemId, type PlayerInventory, type Stack } from './inventory';
import { clearUnlocks } from './unlocks';
import type { Builder, SavedBuild } from './building';
import type { DeployManager, SavedDeploy } from './deployables';
import type { ChestManager, SavedChest } from './chest';
import type { CampfireManager, SavedCampfire } from './campfire';
import type { FarmingManager, SavedPlanter } from './farming';
import type { DroppedItemField, SavedDrop } from './dropped';
import type { ForageField, SavedForage } from './forage';
import type { NodeField, SavedNodes } from './nodes';
import type { AssetField, SavedSalvage } from './assetField';
import type { AnimalField, SavedAnimal } from './animals';
import type { Player } from './player';
import type { DayNight } from '../engine/daynight';

// LOCAL WORLD PERSISTENCE (single-player). A reload used to erase everything; this snapshots the
// mutable world into ONE JSON blob and restores it on boot BEFORE the first frame. Restoring is a
// REPLAY through the same place()/deploy() paths a live session uses (see the per-system
// serialize/deserialize methods), so mirrors, colliders, wall stacks, room graphs and planter hooks
// all regenerate -- no field-poking of derived state. Storage is IndexedDB (survives big bases) with
// a SYNCHRONOUS localStorage flush on tab close (IndexedDB writes can be dropped mid-unload); load
// picks whichever copy is newer. Keyed by the terrain seed, version-stamped: a save from another
// schema or seed is ignored, never half-parsed. NOT wired when ?mp=1 -- the server owns state there.
//
// The procedural fields (forage/nodes/salvage/animals) persist as DELTAS from their deterministic
// layouts: picked/harvested/salvaged spots + their respawn debts, so a picked pumpkin stays gone until
// its real regrow time. Respawn debts are stored as REMAINING seconds (the per-boot clock restarts at
// zero, so absolute deadlines would be meaningless); tree growth stores plantDay absolute because the
// day clock itself is part of the save. All four sections are OPTIONAL so pre-existing saves load.
//
// Deliberately NOT persisted (all cheap to regrow / documented as per-session): tool durability
// (durability.ts documents fresh-per-session), craft queue, station cook/burn progress
// (seconds-scale), bed respawn cooldowns, corpses.

export interface WorldSaveV1 {
  v: number;
  seed: number;
  savedAt: number; // ms epoch; newer of IndexedDB vs localStorage wins at load
  clockSeconds: number; // dayNight total world-clock seconds (dayCount + phase recover from it)
  player: { x: number; y: number; z: number; yaw: number; health: number; hunger: number; thirst: number };
  inventory: { grid: Array<Stack | null>; bar: Array<Stack | null> };
  build: SavedBuild;
  deploy: SavedDeploy;
  chests: SavedChest[];
  campfires: SavedCampfire[];
  planters: SavedPlanter[];
  drops: SavedDrop[];
  // Optional (added after v1 shipped, same version stamp): absent in old saves = pristine fields.
  forage?: SavedForage[];
  nodes?: SavedNodes;
  salvage?: SavedSalvage[];
  animals?: SavedAnimal[];
}

interface PersistDeps {
  builder: Builder;
  deployables: DeployManager;
  chests: ChestManager;
  campfires: CampfireManager;
  farming: FarmingManager;
  dropped: DroppedItemField;
  forage: ForageField;
  nodes: NodeField;
  salvage: AssetField;
  animals: AnimalField;
  inventory: PlayerInventory;
  player: Player;
  dayNight: DayNight;
}

// The seed is read at call time, not module load: the admin panel can regenerate the world live and
// a later save then files under the new world's key instead of clobbering the old one.
function saveKey(): string { return `oxide.world.v${PERSIST.version}.${TERRAIN.seed}`; }

// --- IndexedDB plumbing (one db, one keyed store). Every call is failure-tolerant: private mode /
// storage pressure degrade to the localStorage layer instead of breaking the game. ---
const DB_NAME = 'oxide-world';
const DB_STORE = 'saves';

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(DB_STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
function idbRequest<T>(db: IDBDatabase, mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const req = op(db.transaction(DB_STORE, mode).objectStore(DB_STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGet(key: string): Promise<unknown> {
  const db = await idbOpen();
  try { return await idbRequest(db, 'readonly', (s) => s.get(key)); } finally { db.close(); }
}
async function idbPut(key: string, value: unknown): Promise<void> {
  const db = await idbOpen();
  try { await idbRequest(db, 'readwrite', (s) => s.put(value, key)); } finally { db.close(); }
}
async function idbDelete(key: string): Promise<void> {
  const db = await idbOpen();
  try { await idbRequest(db, 'readwrite', (s) => s.delete(key)); } finally { db.close(); }
}

// A blob is trusted only if the stamp, seed and every top-level section are present -- anything else
// is treated as "no save" (schema bumps and reseeded worlds start fresh by design).
function validSave(s: unknown): s is WorldSaveV1 {
  const v = s as WorldSaveV1 | null;
  return !!v && v.v === PERSIST.version && v.seed === TERRAIN.seed
    && !!v.build && Array.isArray(v.build.pieces) && !!v.deploy && Array.isArray(v.deploy.items)
    && Array.isArray(v.chests) && Array.isArray(v.campfires) && Array.isArray(v.planters) && Array.isArray(v.drops)
    && !!v.player && !!v.inventory && Array.isArray(v.inventory.grid) && Array.isArray(v.inventory.bar);
}

// Read the saved world for the current seed, preferring whichever copy (IndexedDB vs the unload
// localStorage flush) was written last. Null = no usable save (fresh world).
export async function loadWorldSave(): Promise<WorldSaveV1 | null> {
  let fromIdb: WorldSaveV1 | null = null;
  try { const raw = await idbGet(saveKey()); if (validSave(raw)) fromIdb = raw; } catch { /* idb unavailable */ }
  let fromLocal: WorldSaveV1 | null = null;
  try {
    const raw = localStorage.getItem(saveKey());
    if (raw) { const parsed: unknown = JSON.parse(raw); if (validSave(parsed)) fromLocal = parsed; }
  } catch { /* quota / corrupt json */ }
  if (fromIdb && fromLocal) return fromLocal.savedAt > fromIdb.savedAt ? fromLocal : fromIdb;
  return fromIdb ?? fromLocal;
}

export class WorldPersistence {
  private wiping = false; // Fresh Start pressed: block the unload flush from re-writing the save

  constructor(private readonly deps: PersistDeps) {}

  // Snapshot the whole mutable world (called every autosave tick + on unload).
  collect(): WorldSaveV1 {
    const d = this.deps;
    return {
      v: PERSIST.version,
      seed: TERRAIN.seed,
      savedAt: Date.now(),
      clockSeconds: d.dayNight.clockSeconds,
      player: {
        x: d.player.position.x, y: d.player.position.y, z: d.player.position.z,
        yaw: d.player.object.rotation.y,
        health: d.player.health, hunger: d.player.hunger, thirst: d.player.thirst,
      },
      inventory: { grid: d.inventory.serializeSlots(), bar: d.inventory.serializeBar() },
      build: d.builder.serializeWorld(),
      deploy: d.deployables.serialize(),
      chests: d.chests.serialize(),
      campfires: d.campfires.serialize(),
      planters: d.farming.serialize(),
      drops: d.dropped.serialize(),
      forage: d.forage.serialize(),
      nodes: d.nodes.serialize(),
      salvage: d.salvage.serialize(),
      animals: d.animals.serialize(),
    };
  }

  // Rebuild the world from a save. Order matters: built pieces first (doors need empty swing arcs and
  // mounts need their host id remap), then deployables (planter records register here), then the
  // legacy managers, crops, drops, and finally the player + clock.
  apply(save: WorldSaveV1): void {
    const d = this.deps;
    const remap = d.builder.deserializeWorld(save.build);
    d.deployables.deserialize(save.deploy, (old) => remap.get(old));
    for (const w of save.deploy.windows) d.builder.markWindowOccupied(w.x, w.y, w.z); // refill the wall sockets
    d.chests.deserialize(save.chests);
    d.campfires.deserialize(save.campfires);
    d.farming.deserialize(save.planters);
    // Procedural-field deltas (optional sections: old saves skip them and boot pristine fields).
    if (save.forage) d.forage.deserialize(save.forage);
    if (save.nodes) d.nodes.deserialize(save.nodes);
    if (save.salvage) d.salvage.deserialize(save.salvage);
    if (save.animals) d.animals.deserialize(save.animals);
    for (const dr of save.drops) if (dr.id in ITEMS) d.dropped.restore(dr.id as ItemId, dr.n, dr.x, dr.z, dr.age);
    d.inventory.deserializeSlots(save.inventory.grid);
    d.inventory.deserializeBar(save.inventory.bar);
    d.player.position.set(save.player.x, save.player.y, save.player.z);
    d.player.object.position.copy(d.player.position);
    d.player.object.rotation.y = save.player.yaw;
    // Vitals clamp to their live maxima; health floors at 1 so a save taken while dying can't boot a corpse.
    d.player.health = Math.min(PLAYER.maxHealth, Math.max(1, save.player.health));
    d.player.hunger = Math.min(SURVIVAL.maxHunger, Math.max(0, save.player.hunger));
    d.player.thirst = Math.min(SURVIVAL.maxThirst, Math.max(0, save.player.thirst));
    d.dayNight.syncTo(Math.max(0, save.clockSeconds));
  }

  // Write the snapshot to IndexedDB; if that layer is unavailable, the localStorage copy is the save.
  async save(): Promise<void> {
    if (this.wiping) return;
    const blob = this.collect();
    try { await idbPut(saveKey(), blob); }
    catch { this.flushLocal(blob); }
  }

  // Autosave loop + the tab-close flush. The flush must be SYNCHRONOUS (localStorage), because an
  // in-flight IndexedDB write can be dropped when the page unloads.
  startAutosave(): void {
    window.setInterval(() => { void this.save(); }, PERSIST.autosaveSec * 1000);
    window.addEventListener('beforeunload', () => { if (!this.wiping) this.flushLocal(this.collect()); });
  }

  private flushLocal(blob: WorldSaveV1): void {
    try { localStorage.setItem(saveKey(), JSON.stringify(blob)); }
    catch { /* quota/private mode: the periodic IndexedDB save still stands */ }
  }

  // Pause-menu Fresh Start: wipe BOTH copies, then reload into a brand-new world. The idb delete is
  // awaited before the reload so a stale copy can't win the next boot's newer-of race.
  freshStart(): void {
    this.wiping = true;
    try { localStorage.removeItem(saveKey()); } catch { /* nothing to remove */ }
    // Research unlocks reset with the world: a fresh start means the full progression from zero
    // (also purges saves contaminated by the old persist-everything #test bug).
    clearUnlocks();
    idbDelete(saveKey()).catch(() => { /* idb unavailable = nothing stored there */ }).finally(() => location.reload());
  }
}
